import React, { useState, useEffect, useCallback } from "react";
import { teksAPI, unitAPI, pedagogyAPI } from "../../services/api";

// Convert a text[] from DB to a newline-separated string for textarea
function arrToText(arr) {
  if (!arr || !Array.isArray(arr)) return "";
  return arr.join("\n");
}

// Convert newline-separated textarea value back to a text[] for the DB
function textToArr(str) {
  if (!str) return [];
  return str
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);
}

const EMPTY_FORM = {
  misconceptions: "",
  academic_vocabulary_prior: "",
  academic_vocabulary_lesson: "",
  academic_vocabulary_supporting: "",
};

const EMPTY_OVERRIDE_EXTRA = {
  notes: "",
  source_label: "",
};

function PedagogyTab({ courseId }) {
  const [view, setView] = useState("teks"); // "teks" | "unit"

  // Selectors
  const [teksList, setTeksList] = useState([]);
  const [unitsList, setUnitsList] = useState([]);
  const [selectedTeksId, setSelectedTeksId] = useState("");
  const [selectedUnitId, setSelectedUnitId] = useState("");

  // Loading
  const [loadingSelectors, setLoadingSelectors] = useState(true);
  const [loadingRecord, setLoadingRecord] = useState(false);

  // Current record
  const [record, setRecord] = useState(null); // pedagogy row or unit_override row
  const [form, setForm] = useState(EMPTY_FORM);
  const [overrideExtra, setOverrideExtra] = useState(EMPTY_OVERRIDE_EXTRA);

  // Overrides for badge display (teksId → boolean)
  const [overrideSet, setOverrideSet] = useState(new Set());

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Load TEKS + Units for the course
  useEffect(() => {
    if (!courseId) return;
    setLoadingSelectors(true);
    setSelectedTeksId("");
    setSelectedUnitId("");
    setRecord(null);
    setForm(EMPTY_FORM);
    setOverrideExtra(EMPTY_OVERRIDE_EXTRA);
    setOverrideSet(new Set());

    Promise.all([
      teksAPI.getAll(courseId).then((r) => r.data || []),
      unitAPI.getAll(courseId).then((r) => r.data || []),
    ])
      .then(([teks, units]) => {
        const sortedTeks = [...teks].sort((a, b) =>
          String(a.teks_code || "").localeCompare(String(b.teks_code || ""))
        );
        const sortedUnits = [...units].sort(
          (a, b) => (a.unit_number || 0) - (b.unit_number || 0)
        );
        setTeksList(sortedTeks);
        setUnitsList(sortedUnits);
        if (sortedTeks.length > 0) setSelectedTeksId(sortedTeks[0].id);
        if (sortedUnits.length > 0) setSelectedUnitId(sortedUnits[0].id);
      })
      .catch(() => {})
      .finally(() => setLoadingSelectors(false));
  }, [courseId]);

  // Load overrides for selected unit (for badge display)
  useEffect(() => {
    if (!selectedUnitId) {
      setOverrideSet(new Set());
      return;
    }
    pedagogyAPI
      .getUnitOverrides({ unitId: selectedUnitId })
      .then((r) => {
        const ids = new Set((r.data || []).map((o) => o.teks_id));
        setOverrideSet(ids);
      })
      .catch(() => setOverrideSet(new Set()));
  }, [selectedUnitId]);

  // Load base pedagogy or unit override when selectors change
  const loadRecord = useCallback(async () => {
    const teksId = selectedTeksId;
    const unitId = selectedUnitId;
    if (!teksId) {
      setRecord(null);
      setForm(EMPTY_FORM);
      setOverrideExtra(EMPTY_OVERRIDE_EXTRA);
      setDirty(false);
      return;
    }
    setLoadingRecord(true);
    setSaveError("");
    setSaveSuccess(false);
    try {
      if (view === "teks") {
        const res = await pedagogyAPI.getByTeks(teksId);
        const row = res.data || null;
        setRecord(row);
        setForm(
          row
            ? {
                misconceptions: row.misconceptions || "",
                academic_vocabulary_prior: arrToText(row.academic_vocabulary_prior),
                academic_vocabulary_lesson: arrToText(row.academic_vocabulary_lesson),
                academic_vocabulary_supporting: arrToText(
                  row.academic_vocabulary_supporting
                ),
              }
            : EMPTY_FORM
        );
      } else {
        // unit override view
        if (!unitId) {
          setRecord(null);
          setForm(EMPTY_FORM);
          setOverrideExtra(EMPTY_OVERRIDE_EXTRA);
          setDirty(false);
          setLoadingRecord(false);
          return;
        }
        const res = await pedagogyAPI.getUnitOverrides({ teksId, unitId });
        const row = (res.data || [])[0] || null;
        setRecord(row);
        setForm(
          row
            ? {
                misconceptions: row.misconceptions || "",
                academic_vocabulary_prior: arrToText(row.academic_vocabulary_prior),
                academic_vocabulary_lesson: arrToText(row.academic_vocabulary_lesson),
                academic_vocabulary_supporting: arrToText(
                  row.academic_vocabulary_supporting
                ),
              }
            : EMPTY_FORM
        );
        setOverrideExtra(
          row
            ? {
                notes: row.notes || "",
                source_label: row.source_label || "",
              }
            : EMPTY_OVERRIDE_EXTRA
        );
      }
    } catch {
      setRecord(null);
      setForm(EMPTY_FORM);
    } finally {
      setLoadingRecord(false);
      setDirty(false);
    }
  }, [view, selectedTeksId, selectedUnitId]);

  useEffect(() => {
    loadRecord();
  }, [loadRecord]);

  function handleFormChange(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
    setSaveSuccess(false);
  }

  function handleOverrideExtraChange(field, value) {
    setOverrideExtra((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
    setSaveSuccess(false);
  }

  async function handleSave() {
    if (!selectedTeksId) return;
    setSaving(true);
    setSaveError("");
    setSaveSuccess(false);

    const payload = {
      misconceptions: form.misconceptions || null,
      academic_vocabulary_prior: textToArr(form.academic_vocabulary_prior),
      academic_vocabulary_lesson: textToArr(form.academic_vocabulary_lesson),
      academic_vocabulary_supporting: textToArr(form.academic_vocabulary_supporting),
    };

    try {
      if (view === "teks") {
        if (record) {
          const res = await pedagogyAPI.update(record.id, payload);
          setRecord(res.data);
        } else {
          const res = await pedagogyAPI.create({ teks_id: selectedTeksId, ...payload });
          setRecord(res.data);
        }
      } else {
        const overridePayload = {
          ...payload,
          notes: overrideExtra.notes || null,
          source_label: overrideExtra.source_label || null,
        };
        if (record) {
          const res = await pedagogyAPI.updateUnitOverride(record.id, overridePayload);
          setRecord(res.data);
        } else {
          const res = await pedagogyAPI.createUnitOverride({
            teks_id: selectedTeksId,
            unit_id: selectedUnitId,
            ...overridePayload,
          });
          setRecord(res.data);
          // Update badge set
          setOverrideSet((prev) => new Set([...prev, selectedTeksId]));
        }
      }
      setSaveSuccess(true);
      setDirty(false);
    } catch (err) {
      setSaveError(err?.response?.data?.error || "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  function handleReset() {
    loadRecord();
  }

  if (!courseId) {
    return <p className="curriculum-empty">Select a course to edit pedagogy.</p>;
  }

  if (loadingSelectors) {
    return <p className="curriculum-empty">Loading TEKS and units...</p>;
  }

  if (teksList.length === 0) {
    return (
      <p className="curriculum-empty">
        No TEKS found for this course. Add TEKS in the TEKS tab first.
      </p>
    );
  }

  return (
    <div className="pedagogy-tab">
      {/* Segmented control */}
      <div className="pedagogy-view-toggle">
        <button
          className={`pedagogy-view-btn${view === "teks" ? " active" : ""}`}
          onClick={() => setView("teks")}
        >
          TEKS-Level Pedagogy
        </button>
        <button
          className={`pedagogy-view-btn${view === "unit" ? " active" : ""}`}
          onClick={() => setView("unit")}
          disabled={unitsList.length === 0}
          title={unitsList.length === 0 ? "No units in this course" : undefined}
        >
          Unit Override
        </button>
      </div>

      {/* Selectors */}
      <div className="pedagogy-selectors">
        <label className="pedagogy-selector-label">
          TEKS
          <select
            value={selectedTeksId}
            onChange={(e) => setSelectedTeksId(e.target.value)}
            className="pedagogy-selector-select"
          >
            {teksList.map((t) => (
              <option key={t.id} value={t.id}>
                {t.teks_code}
                {view === "unit" && overrideSet.has(t.id) ? " ★" : ""}
              </option>
            ))}
          </select>
        </label>

        {view === "unit" && (
          <label className="pedagogy-selector-label">
            Unit
            <select
              value={selectedUnitId}
              onChange={(e) => setSelectedUnitId(e.target.value)}
              className="pedagogy-selector-select"
            >
              {unitsList.map((u) => (
                <option key={u.id} value={u.id}>
                  Unit {u.unit_number}: {u.title}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {/* Selected TEKS description */}
      {selectedTeksId && (() => {
        const t = teksList.find((t) => t.id === selectedTeksId);
        return t ? (
          <p className="pedagogy-teks-description">
            <strong>{t.teks_code}</strong> — {t.description}
          </p>
        ) : null;
      })()}

      {/* Form */}
      {loadingRecord ? (
        <p className="pedagogy-loading">Loading...</p>
      ) : (
        <div className="pedagogy-form">
          {view === "unit" && !record && (
            <p className="pedagogy-no-override-note">
              No unit override yet for this TEKS + unit combination. Fill in the fields below to create one.
            </p>
          )}

          <div className="pedagogy-field">
            <label className="pedagogy-field-label" htmlFor="pedagogy-misconceptions">
              Misconceptions
            </label>
            <textarea
              id="pedagogy-misconceptions"
              className="pedagogy-textarea"
              rows={4}
              value={form.misconceptions}
              onChange={(e) => handleFormChange("misconceptions", e.target.value)}
              placeholder="Describe common student misconceptions for this standard..."
            />
          </div>

          <div className="pedagogy-field">
            <label className="pedagogy-field-label" htmlFor="pedagogy-vocab-prior">
              Academic Vocabulary — Prior Knowledge
              <span className="pedagogy-field-hint">(one term per line)</span>
            </label>
            <textarea
              id="pedagogy-vocab-prior"
              className="pedagogy-textarea"
              rows={4}
              value={form.academic_vocabulary_prior}
              onChange={(e) =>
                handleFormChange("academic_vocabulary_prior", e.target.value)
              }
              placeholder="energy&#10;force&#10;matter"
            />
          </div>

          <div className="pedagogy-field">
            <label className="pedagogy-field-label" htmlFor="pedagogy-vocab-lesson">
              Academic Vocabulary — This Lesson
              <span className="pedagogy-field-hint">(one term per line)</span>
            </label>
            <textarea
              id="pedagogy-vocab-lesson"
              className="pedagogy-textarea"
              rows={4}
              value={form.academic_vocabulary_lesson}
              onChange={(e) =>
                handleFormChange("academic_vocabulary_lesson", e.target.value)
              }
              placeholder="kinetic energy&#10;potential energy"
            />
          </div>

          <div className="pedagogy-field">
            <label
              className="pedagogy-field-label"
              htmlFor="pedagogy-vocab-supporting"
            >
              Academic Vocabulary — Supporting
              <span className="pedagogy-field-hint">(one term per line)</span>
            </label>
            <textarea
              id="pedagogy-vocab-supporting"
              className="pedagogy-textarea"
              rows={4}
              value={form.academic_vocabulary_supporting}
              onChange={(e) =>
                handleFormChange("academic_vocabulary_supporting", e.target.value)
              }
              placeholder="joule&#10;conservation"
            />
          </div>

          {view === "unit" && (
            <>
              <div className="pedagogy-field">
                <label className="pedagogy-field-label" htmlFor="pedagogy-notes">
                  Unit Override Notes
                </label>
                <textarea
                  id="pedagogy-notes"
                  className="pedagogy-textarea"
                  rows={3}
                  value={overrideExtra.notes}
                  onChange={(e) => handleOverrideExtraChange("notes", e.target.value)}
                  placeholder="Notes specific to this unit's treatment of the standard..."
                />
              </div>

              <div className="pedagogy-field">
                <label className="pedagogy-field-label" htmlFor="pedagogy-source">
                  Source Label
                </label>
                <input
                  id="pedagogy-source"
                  type="text"
                  className="pedagogy-input"
                  value={overrideExtra.source_label}
                  onChange={(e) =>
                    handleOverrideExtraChange("source_label", e.target.value)
                  }
                  placeholder="e.g. Unit 3 pacing guide"
                />
              </div>
            </>
          )}

          <div className="pedagogy-actions">
            <button
              className="pedagogy-save-btn"
              onClick={handleSave}
              disabled={saving || !dirty}
            >
              {saving ? "Saving..." : record ? "Save Changes" : "Create Record"}
            </button>
            <button
              className="pedagogy-reset-btn"
              onClick={handleReset}
              disabled={saving || !dirty}
            >
              Reset
            </button>
            {saveSuccess && (
              <span className="pedagogy-save-success">Saved successfully.</span>
            )}
            {saveError && <span className="pedagogy-save-error">{saveError}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

export default PedagogyTab;
